/**
 * Interpreting an event's `location` field.
 *
 * `location` is free text, and calendar imports put things in it that are not places: a
 * third-party event page, a Meet/Zoom room that *is* the venue of an online event, or nothing at
 * all (Luma hidden-address imports store '' — backend PR #755). Every surface that reads it needs
 * the same three answers — is there anything here, is it a link, and what should be shown — so
 * they live together and the surfaces cannot drift apart.
 *
 * Building a maps URL is a separate question, answered by `./mapsLauncher`.
 */
import { getPlatformName } from './platformDetection';

/**
 * True when the event carries a location worth rendering at all.
 *
 * A blank value must render nothing — not an empty row, not a stray map-pin icon, not a
 * separator with no neighbour.
 */
export function hasLocation(location: string | null | undefined): boolean {
  return (location ?? '').trim().length > 0;
}

/**
 * The location as an openable web link, or `null` when it is an ordinary place string.
 *
 * The backend keeps URL locations on purpose (PR #755 only blanked Luma's self-referential
 * hidden-address marker), so callers must branch on this before offering a maps launcher — a maps
 * provider handed a URL just searches for the URL text.
 *
 * Only `http:` and `https:` qualify. `javascript:`, `data:` and every other scheme return `null`,
 * so a location string can never be rendered as an executable link. Same policy as `isSafeUrl`
 * in `./url` — kept inline here to avoid a second `new URL()` parse per card render.
 */
export function getLocationUrl(location: string | null | undefined): string | null {
  const trimmed = (location ?? '').trim();
  if (!trimmed) return null;

  // Cheap gate before the parse, for two reasons. Correctness: `new URL()` also accepts a URL
  // with human text glued on ('https://lu.ma/x Hall 2, Lisboa' percent-encodes the address into
  // the path), which would classify a place-with-a-link as a pure link and destroy the address
  // half. Cost: almost every location is a plain place name, and reaching the `catch` means
  // constructing a thrown TypeError once per card per render.
  if (!/^https?:\/\/\S+$/i.test(trimmed)) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
}

/**
 * What to show the user for this location, or `''` when there is nothing to show.
 *
 * A URL location becomes the platform behind it — "Luma", "Zoom", "Google Meet", or the bare host
 * for anything unrecognised — because a raw `https://lu.ma/xyz` reads as broken next to a map pin,
 * especially on cards where it cannot even be tapped. Everything else is already a place name and
 * passes through.
 *
 * `EventAboutTab` deliberately does NOT use this. A card abbreviates to the brand because its
 * location label opens nothing; the detail page is where someone decides whether to tap through to
 * a third party, so it discloses the destination host next to the brand.
 *
 * That split is also the containment: the surface with the weaker label is the surface whose label
 * opens nothing. `MULTI_TLD_PLATFORMS` in `./platformDetection` only approximates a public suffix,
 * so eventbrite.io and eventbrite.tk still wear the brand here. It matters that the only surface
 * offering to open the link is the one that names the host.
 */
export function getLocationLabel(location: string | null | undefined): string {
  const url = getLocationUrl(location);
  // getPlatformName's own fallback is the (www-stripped, path-less) hostname, capitalized.
  return url === null ? (location ?? '').trim() : getPlatformName(url);
}
